import { Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { CHAPTERS } from '../data/chapters';
import { PERSONAS, type TutorialPersona } from '../data/tutorial-data';

interface ChapterNavProps {
  currentChapter: number;
  completedChapters: Set<string>;
  onNavigate: (index: number) => void;
}

function PersonaAvatar({
  persona,
  size = 'sm',
}: {
  persona: TutorialPersona | null;
  size?: 'sm' | 'md';
}) {
  if (!persona) return null;
  const sizeClass = size === 'sm' ? 'w-5 h-5 text-[9px]' : 'w-6 h-6 text-[10px]';
  return (
    <div
      className={cn(
        'rounded-full flex items-center justify-center text-white font-bold',
        persona.color,
        sizeClass
      )}
    >
      {persona.initials}
    </div>
  );
}

export function ChapterNav({ currentChapter, completedChapters, onNavigate }: ChapterNavProps) {
  return (
    <div className="w-full overflow-x-auto pb-2">
      <div className="flex items-center justify-between min-w-[600px] px-4">
        {CHAPTERS.map((chapter, i) => {
          const isActive = i === currentChapter;
          const isCompleted = completedChapters.has(chapter.id);
          const persona = chapter.persona ? PERSONAS[chapter.persona] : null;

          return (
            <div key={chapter.id} className="flex items-center flex-1 last:flex-none">
              {/* Circle */}
              <button
                onClick={() => onNavigate(i)}
                className={cn(
                  'relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                  isActive && 'bg-primary text-primary-foreground ring-4 ring-primary/20',
                  isCompleted && !isActive && 'bg-green-500 text-white',
                  !isActive && !isCompleted && 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
                title={chapter.title}
              >
                {isCompleted && !isActive ? <Check className="w-4 h-4" /> : i}
              </button>

              {/* Label (desktop) */}
              <div className="hidden xl:flex flex-col ml-2 mr-2 min-w-0">
                <span
                  className={cn(
                    'text-xs font-medium truncate',
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {chapter.title}
                </span>
                {persona && <PersonaAvatar persona={persona} size="sm" />}
              </div>

              {/* Connecting line */}
              {i < CHAPTERS.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2',
                    completedChapters.has(CHAPTERS[i + 1]?.id) || i < currentChapter
                      ? 'bg-green-500'
                      : 'bg-muted'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { PersonaAvatar };
